import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { Box } from '@/UI/Box/Box';

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export enum TurnstileWidgetTheme {
  Light = 'light',
  Dark = 'dark',
  Auto = 'auto',
}

export enum TurnstileWidgetAppearance {
  Always = 'always',
  InteractionOnly = 'interaction-only',
}

/** `Normal` is Cloudflare's fixed 300px widget. `Flexible` fills the width of its container. */
export enum TurnstileWidgetSize {
  Normal = 'normal',
  Flexible = 'flexible',
}

export interface TurnstileRenderOptions {
  sitekey: string;
  action: string;
  appearance: TurnstileWidgetAppearance;
  execution: 'render';
  retry: 'auto';
  'refresh-expired': 'auto';
  'refresh-timeout': 'auto';
  'response-field': false;
  theme: TurnstileWidgetTheme;
  size: TurnstileWidgetSize;
  callback: (token: string, clearanceCookieIssued?: unknown) => void;
  'error-callback': (errorCode: string) => void;
  'expired-callback': () => void;
  'timeout-callback': () => void;
  'unsupported-callback': () => void;
  'before-interactive-callback': () => void;
  'after-interactive-callback': () => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
  getResponse?: (widgetId: string) => string | undefined;
}

export interface TurnstileWidgetHandle {
  reset: () => void;
}

interface TurnstileWidgetProps {
  sitekey: string;
  action: string;
  appearance: TurnstileWidgetAppearance;
  theme?: TurnstileWidgetTheme;
  size?: TurnstileWidgetSize;
  onSuccess: (token: string, clearanceCookieIssued?: boolean) => void;
  onError?: (errorCode: string) => void;
  onUnavailable?: () => void;
  onExpire?: () => void;
  onTimeout?: () => void;
  onUnsupported?: () => void;
  onBeforeInteractive?: () => void;
  onAfterInteractive?: () => void;
  onScriptLoaded?: (wasScriptAlreadyLoaded: boolean) => void;
  onRendered?: (readToken: () => string | undefined) => void;
  'data-testid'?: string;
}

// Cloudflare Turnstile client error codes are 6-digit strings. A shape check, not an allowlist: window.turnstile is
// page-controllable and any 6-digit string passes, so the result is not safe as a metric tag without further bounding.
const TURNSTILE_ERROR_CODE_PATTERN = /^\d{6}$/;

export const toBoundedTurnstileErrorCode = (errorCode: string): string =>
  TURNSTILE_ERROR_CODE_PATTERN.test(errorCode) ? errorCode : 'unknown';

let scriptLoadPromise: Promise<TurnstileApi> | undefined;

// No load timeout: a slow network gets as long as it needs, and callers surface the wait. A failure drops the promise
// and the element so the next caller re-injects, since a script fires load/error only once.
const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise<TurnstileApi>((resolve, reject) => {
    let script = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    const fail = (error: Error): void => {
      scriptLoadPromise = undefined;
      document.getElementById(TURNSTILE_SCRIPT_ID)?.remove();
      reject(error);
    };
    script.addEventListener(
      'load',
      () =>
        window.turnstile
          ? resolve(window.turnstile)
          : fail(new Error('Turnstile API is unavailable after script load')),
      { once: true },
    );
    script.addEventListener('error', () => fail(new Error('Turnstile script failed to load')), { once: true });
  });
  return scriptLoadPromise;
};

/** Test-only: resets the shared script-injection state so the next mount re-injects the Turnstile script. */
export function TestOnlyResetTurnstileScriptLoader(): void {
  scriptLoadPromise = undefined;
}

export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(function TurnstileWidget(
  {
    sitekey,
    action,
    appearance,
    theme = TurnstileWidgetTheme.Auto,
    size = TurnstileWidgetSize.Normal,
    onSuccess,
    onError,
    onUnavailable,
    onExpire,
    onTimeout,
    onUnsupported,
    onBeforeInteractive,
    onAfterInteractive,
    onScriptLoaded,
    onRendered,
    'data-testid': dataTestId = 'turnstile-widget',
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);
  const apiRef = useRef<TurnstileApi | undefined>(undefined);

  const callbacksRef = useRef({
    onSuccess,
    onError,
    onUnavailable,
    onExpire,
    onTimeout,
    onUnsupported,
    onBeforeInteractive,
    onAfterInteractive,
    onScriptLoaded,
    onRendered,
  });
  useEffect(() => {
    callbacksRef.current = {
      onSuccess,
      onError,
      onUnavailable,
      onExpire,
      onTimeout,
      onUnsupported,
      onBeforeInteractive,
      onAfterInteractive,
      onScriptLoaded,
      onRendered,
    };
  });

  const reset = useCallback((): void => {
    const api = apiRef.current;
    const widgetId = widgetIdRef.current;
    if (!api || widgetId === undefined) return;
    try {
      api.reset(widgetId);
    } catch {
      // A failed reset leaves the widget as it was; the caller already treats the token as gone.
    }
  }, []);

  useImperativeHandle(ref, () => ({ reset }), [reset]);

  useEffect(() => {
    if (!sitekey) return;

    let isMounted = true;
    const container = containerRef.current;

    const renderWidget = async () => {
      try {
        const wasScriptAlreadyLoaded = window.turnstile !== undefined;
        const api = await loadTurnstile();
        if (!isMounted || !container) return;
        try {
          callbacksRef.current.onScriptLoaded?.(wasScriptAlreadyLoaded);
        } catch {
          // A diagnostics hook must never fail a live widget.
        }
        apiRef.current = api;
        const renderedWidgetId = api.render(container, {
          sitekey,
          action,
          appearance,
          execution: 'render',
          retry: 'auto',
          'refresh-expired': 'auto',
          'refresh-timeout': 'auto',
          'response-field': false,
          theme,
          size,
          // Gate on isMounted: Cloudflare can fire a callback after teardown (esp. if remove() failed), and
          // the handler may act on state that has since moved on — a late fire must be a no-op.
          // The second argument is undocumented: whether Turnstile's pre-clearance request set the cf_clearance cookie.
          callback: (token, clearanceCookieIssued) =>
            isMounted &&
            (typeof clearanceCookieIssued === 'boolean'
              ? callbacksRef.current.onSuccess(token, clearanceCookieIssued)
              : callbacksRef.current.onSuccess(token)),
          'error-callback': (errorCode) => isMounted && callbacksRef.current.onError?.(errorCode),
          'expired-callback': () => isMounted && callbacksRef.current.onExpire?.(),
          'timeout-callback': () => isMounted && callbacksRef.current.onTimeout?.(),
          'unsupported-callback': () => isMounted && callbacksRef.current.onUnsupported?.(),
          'before-interactive-callback': () => isMounted && callbacksRef.current.onBeforeInteractive?.(),
          'after-interactive-callback': () => isMounted && callbacksRef.current.onAfterInteractive?.(),
        });
        widgetIdRef.current = renderedWidgetId;
        const readToken = (): string | undefined => api.getResponse?.(renderedWidgetId);
        try {
          callbacksRef.current.onRendered?.(readToken);
        } catch {
          // A diagnostics hook must never fail a live widget.
        }
      } catch {
        if (isMounted) callbacksRef.current.onUnavailable?.();
      }
    };

    void renderWidget();

    return () => {
      isMounted = false;
      const api = apiRef.current;
      const widgetId = widgetIdRef.current;
      if (api && widgetId !== undefined) {
        // Guard teardown like render/reset: an unmount that throws would escape into React's cleanup and,
        // for a widget mounted at the app root, take down the whole app via the global ErrorBoundary.
        try {
          api.remove(widgetId);
        } catch {
          // no-op
        }
      }
      apiRef.current = undefined;
      widgetIdRef.current = undefined;
    };
  }, [sitekey, action, appearance, theme, size]);

  return (
    <Box
      ref={containerRef}
      data-testid={dataTestId}
      sx={{ minHeight: '65px', width: size === TurnstileWidgetSize.Flexible ? '100%' : '300px' }}
    />
  );
});
